import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Building, MapPin, Users, Eye, Filter, ChevronDown, Mail } from 'lucide-react';
import { Button, Input, Select, Modal, Form, Card, Tooltip, Dropdown, Menu } from 'antd';
import { getPlants, createPlant, updatePlant, deletePlant, getAvailableAdmins } from '../../services/plantAPI';
import toast from 'react-hot-toast';

const { Search: AntSearch } = Input;
const { Option } = Select;

const PlantManagement = () => {
  const [plants, setPlants] = useState([]);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlant, setEditingPlant] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [form] = Form.useForm();

  useEffect(() => {
    fetchPlants();
    fetchAvailableAdmins();
  }, [filters, pagination.current]);

  const fetchPlants = async () => {
    try {
      setLoading(true);
      const response = await getPlants({
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search,
        status: filters.status === 'all' ? undefined : filters.status,
      });
    
      setPlants(response.plants);
      setPagination(prev => ({
        ...prev,
        total: response.total,
      }));
    } catch (error) {
      console.error('Error fetching plants:', error);
      toast.error('Failed to fetch plants');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableAdmins = async () => {
    try {
      const admins = await getAvailableAdmins();
      setAvailableAdmins(admins);
    } catch (error) {
      console.error('Error fetching available admins:', error);
    }
  };

  const handleCreatePlant = () => {
    setEditingPlant(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEditPlant = (plant) => {
    setEditingPlant(plant);
    setModalVisible(true);
    form.setFieldsValue({
      ...plant,
      admin: plant.adminId?.email, // Use email for the form
      'address.street': plant.address?.street,
      'address.city': plant.address?.city,
      'address.state': plant.address?.state,
      'address.zipCode': plant.address?.zipCode,
      'address.country': plant.address?.country,
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      const plantData = {
        ...values,
        adminEmail: values.admin, // Pass the email as adminEmail
        address: {
          street: values['address.street'],
          city: values['address.city'],
          state: values['address.state'],
          zipCode: values['address.zipCode'],
          country: values['address.country'],
        },
      };

      // Remove the flat address and admin fields
      delete plantData['address.street'];
      delete plantData['address.city'];
      delete plantData['address.state'];
      delete plantData['address.zipCode'];
      delete plantData['address.country'];
      delete plantData.admin;

      if (editingPlant) {
        await updatePlant(editingPlant._id, plantData);
        toast.success('Plant updated successfully');
      } else {
        await createPlant(plantData);
        toast.success('Plant created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      fetchPlants();
      fetchAvailableAdmins();
    } catch (error) {
      console.error('Error saving plant:', error);
      toast.error(error.response?.data?.message || 'Failed to save plant');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlant = async (plantId) => {
    Modal.confirm({
      title: 'Delete Plant',
      content: 'Are you sure you want to delete this plant? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deletePlant(plantId);
          toast.success('Plant deleted successfully');
          fetchPlants();
          fetchAvailableAdmins();
        } catch (error) {
          console.error('Error deleting plant:', error);
          toast.error(error.response?.data?.message || 'Failed to delete plant');
        }
      },
    });
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const actionMenu = (plant) => (
    <Menu>
      <Menu.Item key="view" icon={<Eye size={14} />}>
        View Details
      </Menu.Item>
      <Menu.Item 
        key="edit" 
        icon={<Edit2 size={14} />}
        onClick={() => handleEditPlant(plant)}
      >
        Edit Plant
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        key="delete" 
        danger
        icon={<Trash2 size={14} />}
        onClick={() => handleDeletePlant(plant._id)}
      >
        Delete Plant
      </Menu.Item>
    </Menu>
  );

  if (loading && plants?.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Plant Management</h1>
            <p className="text-gray-600 mt-2">
              Manage plants, assign administrators, and monitor performance
            </p>
          </div>
          
          <Button 
            type="primary" 
            size="large" 
            icon={<Plus size={18} />}
            onClick={handleCreatePlant}
          >
            Add New Plant
          </Button>
        </div>

        {/* Filters */}
        <Card className="shadow-sm">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <AntSearch
                placeholder="Search plants by name or location..."
                onSearch={handleSearch}
                className="max-w-md"
                enterButton
              />
              
              <Select
                value={filters.status}
                onChange={handleStatusFilter}
                className="w-40"
              >
                <Option value="all">All Status</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
          </div>
        </Card>

        {/* Plants Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {plants?.map((plant) => (
            <Card
              key={plant._id}
              className="shadow-md hover:shadow-lg transition-shadow duration-200"
              
            >
              <div className="space-y-4">
                {/* Plant Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Building className="text-blue-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">
                        {plant.name}
                      </h3>
                      <div className="flex items-center text-gray-500 text-sm mt-1">
                        <MapPin size={14} className="mr-1" />
                        {plant.location}
                      </div>
                    </div>
                  </div>

                  <div className='flex  items-center space-x-2'>
                  <Tooltip title="Delete Plant">
                  <Trash2 size={16} className="text-red-600 cursor-pointer" onClick={() => handleDeletePlant(plant._id)} />
                </Tooltip>
                <Tooltip title="Edit Plant">
                  <Edit2 
                    size={16} 
                    className="text-green-600 cursor-pointer"
                    onClick={() => handleEditPlant(plant)}
                  />
                </Tooltip>
                  </div>
                  
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    plant.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {plant.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>

                {/* Plant Info */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm flex items-center">
                      <Mail size={14} className="mr-1" />
                      Administrator
                    </span>
                    <span className="font-medium text-sm">
                      {plant.adminId?.email || 'Not Assigned'}
                    </span>
                  </div>
                  
                  {plant.contactEmail && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">Contact</span>
                      <span className="text-sm truncate max-w-32" title={plant.contactEmail}>
                        {plant.contactEmail}
                      </span>
                    </div>
                  )}
                  
                  {plant.statistics && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm flex items-center">
                        <Users size={14} className="mr-1" />
                        Employees
                      </span>
                      <span className="font-medium text-sm">
                        {plant.statistics.totalUsers || 0}
                      </span>
                    </div>
                  )}
                  
                  {/* <div className="flex justify-between items-center">
                    <span className="text-gray-500 text-sm">Capacity</span>
                    <span className="font-medium text-sm">
                      {plant.capacity || 'Not specified'}
                    </span>
                  </div> */}
                </div>

                {/* Description */}
                {plant.description && (
                  <div className="pt-2 border-t">
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {plant.description}
                    </p>
                  </div>
                )}

                {/* Footer */}
                <div className="pt-2 border-t text-xs text-gray-400">
                  Created: {new Date(plant.createdAt).toLocaleDateString()}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {plants.length === 0 && !loading && (
          <div className="text-center py-12">
            <Building size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No plants found</h3>
            <p className="text-gray-500 mb-4">
              {filters.search || filters.status !== 'all' 
                ? 'No plants match your current filters.' 
                : 'Get started by creating your first plant.'
              }
            </p>
            <Button type="primary" icon={<Plus size={16} />} onClick={handleCreatePlant}>
              Add New Plant
            </Button>
          </div>
        )}

        {/* Plant Form Modal */}
        <Modal
          title={editingPlant ? 'Edit Plant' : 'Create New Plant'}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          footer={null}
          width={800}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Plant Name"
                name="name"
                rules={[{ required: true, message: 'Please enter plant name' }]}
              >
                <Input placeholder="Enter plant name" />
              </Form.Item>

              <Form.Item
                label="Location"
                name="location"
                rules={[{ required: true, message: 'Please enter location' }]}
              >
                <Input placeholder="Enter location" />
              </Form.Item>
            </div>

            <Form.Item
              label="Administrator Email"
              name="admin" // Change name to "admin" to match the email field
              rules={[
                { required: true, message: 'Please enter an administrator email' },
                { type: 'email', message: 'Please enter a valid email address' },
              ]}
            >
              <Input 
                placeholder="Enter admin email" 
                disabled={!!editingPlant} // Disable if editing a plant
              />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
            >
              <Input.TextArea 
                rows={3} 
                placeholder="Enter plant description (optional)" 
              />
            </Form.Item>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                label="Contact Email"
                name="contactEmail"
              >
                <Input type="email" placeholder="Enter contact email" />
              </Form.Item>

              <Form.Item
                label="Contact Phone"
                name="contactPhone"
              >
                <Input placeholder="Enter contact phone" />
              </Form.Item>
            </div> */}

            {/* <Form.Item
              label="Capacity"
              name="capacity"
            >
              <Input type="number" placeholder="Enter plant capacity" />
            </Form.Item> */}

            {/* Address Section */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Address Details</h4>
              
              <Form.Item
                label="Street Address"
                name="address.street"
              >
                <Input placeholder="Enter street address" />
              </Form.Item>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Form.Item
                  label="City"
                  name="address.city"
                >
                  <Input placeholder="Enter city" />
                </Form.Item>

                <Form.Item
                  label="State"
                  name="address.state"
                >
                  <Input placeholder="Enter state" />
                </Form.Item>

                <Form.Item
                  label="ZIP Code"
                  name="address.zipCode"
                >
                  <Input placeholder="Enter ZIP code" />
                </Form.Item>
              </div>

              <Form.Item
                label="Country"
                name="address.country"
              >
                <Input placeholder="Enter country" />
              </Form.Item>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button 
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
              >
                {editingPlant ? 'Update Plant' : 'Create Plant'}
              </Button>
            </div>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default PlantManagement;